import {ApiInvoiceStatus, PayrollBatch} from './paymaster-batch.model';
import {ApprovalTask, MobileApprovalLoop} from './timecard.model';
import {DocumentMetadata} from './document.model';
import {PayLineStatus, TimecardPayrollStatus, PayLineIssue} from './timecard-models/uk.model';
import {StartcardAccountCodeSegment} from './start-card.model';

export interface Batch {
  id: string;
  name: string;
  date: string;
}

export interface LiteBatch {
  id: string;
  name: string;
}

export interface BatchSummary {
  name: string;
  id: string;
  projectId: string;
  payPeriodEnding: string;
  timecards: number;
  issues: string;
  hasUnsubmittableErrors: boolean;
  hasSubmittableErrors: boolean;
  documentMetadata: DocumentMetadata[];
  approved: string;
  invoice: string;
  status: BatchStatus;
  received: string;
  deliveryMethod: string;
  paymaster: string;
  urlParams?: string[];
  isInternal: boolean;
  createdBy: string;
  payrollBatchDetails: PayrollBatch[];
  payrollStatus?: BatchPayrollStatus;
  hasRevisedTimecardsInTheBatch: boolean;
  pending?: number;
  readyForPayroll?: number;
  sentToPayroll?: number;
  totalAmount?: number;
}

export interface BatchSummaryResponse {
  total?: number;
  batchSummaries: BatchSummary[];
}

export enum BatchStatus {
  SUBMITTED = 'SUBMITTED',
  UNSUBMITTED = 'UNSUBMITTED',
  PROCESSED = 'PROCESSED'
}

export enum BatchError {
  CORRECTABLE= 'CORRECTABLE',
  UNCORRECTABLE= 'UNCORRECTABLE',
  NOERRORS= 'NO ERRORS',
}

export interface BatchTimecardSummaryAmounts {
  basePay: number;
  additionals: number;
  allowances: number;
  other: number;
  deductions: number;
  holiday: number;
  totalTimecard: number;
}

export interface BatchTimecardSummaryUnits {
  basePay: number;
  additionals: number;
  allowances: number;
  other: number;
  deductions: number;
  holiday: number;
}

export interface BatchTimecardPayElement {
  payCode: string;
  payCategory: string;
  payElement: string;
  amount: number;
  units: number;
}

export interface BatchTimecardSummary {
  timecardId: string;
  crewName: string;
  ssn: string;
  fein: string;
  weekEnding: string;
  jobTitle: string;
  department: string;
  accountCode: string;
  days: number;
  amount: number;
  hours: number;
  grossAmount: number;
  issues: string;
  isInternal: boolean;
  discrepancy: boolean;
  hasUnsubmittableErrors: boolean;
  hasSubmittableErrors: boolean;
  keyedBy: string;
  timecardInvoiceStatus: ApiInvoiceStatus;
  queryParams: { batchId: string , weekEndingDate: string };
  batchTimecardSummaryUrlParams?: string[];
  approvalTasks: ApprovalTask[];
  isDisplayOrangeCircle: boolean;
  isApplyOtherDataSources: boolean;
  isMobileApproved: boolean;
  isMobileTimecard: boolean;
  isDailyTimecard: boolean;
  mobileApprovalLoops?: MobileApprovalLoop[];
  hasPayrollExceptions: boolean;
  crewType: string;
  isRevised: string;
  payrollStatus: TimecardPayrollStatus;
  engagementType: string;
  hasPayLineIssues: boolean;
  pending?: number;
  readyForPayroll?: number;
  sentToPayroll?: number;
  notes?: boolean;
  amounts?: BatchTimecardSummaryAmounts;
  units?: BatchTimecardSummaryUnits;
  payElements?: BatchTimecardPayElement[];
}

export interface BatchTimecardTotals {
  sentToPayrollAmount: number;
  readyForPayrollAmount: number;
  pendingAmount: number;
  totalAmount: number;
  paycodeAmountsTotals: Record<string, number>;
  paycodeUnitsTotals: Record<string, number>;
}

export interface BatchTimecardFooterTotals {
  pending: number | null;
  readyForPayroll: number | null;
  sentToPayroll: number | null;
  totalAmount: number | null;
  basePayAmount: number| null;
  basePayUnits: number| null;
  additionalsAmount: number| null;
  additionalsUnits: number | null;
  holidayAmount?: number| null;
  holidayUnits?: number| null;
  allowancesAmount: number| null;
  allowancesUnits: number| null;
  otherAmount: number| null;
  otherUnits: number| null;
  deductionsAmount: number| null;
  deductionsUnits: number| null;
  paycodeAmountsTotals: Record<string, number>;
  paycodeUnitsTotals: Record<string, number>;
}

export interface BatchTimecardSummaryResponse {
  total?: number;
  batchTimecardSummary: BatchTimecardSummary[];
  timecardTotals?: BatchTimecardTotals;
}

export enum BatchDuplicateStatus {
  Completed = 'Completed',
  Processing = 'Processing',
  Failed = 'Failed',
  NotStarted = 'Not Started',
  AlreadyExists = 'Already Exists'
}

export enum BatchPayrollStatus {
  PENDING = 'PENDING',
  PARTIALLY_SENT = 'PARTIALLY SENT',
  SENT_TO_PAYROLL = 'SENT TO PAYROLL'
}

export interface DuplicateBatch {
  id: string;
  name: string;
  payPeriodEnding: string;
  retainTimecard: boolean;
  retainTimecardNotes: boolean;
  status: BatchDuplicateStatus;
}

export interface ErrorTimecard {
  id: string;
  crewName: string;
  ssn: string;
  job: string;
  weekEnding: string;
}

export interface ErrorTimecardResponse {
  total?: number;
  errorTimecards: ErrorTimecard[];
}

export interface BatchNote {
  batchId: string;
  message: string;
  createdBy: string;
  createdTimestamp: string;
}

export interface InjectData {
  hasPayrollExceptions: boolean;
  openExceptions: boolean;
  overridden: boolean;
  overrideReason: string;
}

export interface PayLine {
  payLineId: string;
  payCode: string;
  payElement: string;
  payCategory: string;
  payLineStatus: PayLineStatus;
  payDescription: string;
  invoice: string;
  rateIncludingHoliday: number;
  rateExcludingHoliday: number;
  units: number;
  amountExcludingHoliday: number;
  holiday: number;
  amountIncludingHoliday: number;
  sentDate: string;
  accountCoding: { [header: string]: string; };
  payLineIssues?: PayLineIssue[];
}

export interface PayLineGroup {
  payLineGroupId: string;
  name: string;
  payLines: PayLine[];
}

export interface BatchPayLine {
  total: number;
  readyForPayrollCount: number;
  payLineGroups: PayLineGroup[];
}

export interface PayLineFilterPayload {
  filters: string;
  search: string;
  sort: string;
  tcFilters: string;
  tcSearch: string;
  tcSort: string;
  timecardIds: string[];
}

export interface PayLineBulkUpdateData {
  description: string;
  glValues: Partial<StartcardAccountCodeSegment>[];
  invoice: string;
  payLineIssues: PayLineIssue[];
  payLineStatus: PayLineStatus;
}

export interface BulkUpdatePayload {
  filters: string;
  search: string;
  tcFilters: string;
  tcSearch: string;
  timecardIds: string[];
  payLineIds: string[];
  updateData: PayLineBulkUpdateData;
}

export interface SavePayLinesResponse {
  readyForPayrollCount: number;
  total: number;
}

export interface BatchPayLineSummary {
  total: number;
  readyForPayrollCount: number;
  totalAmount: number;
  sentToPayrollAmount: number;
  readyForPayrollAmount: number;
  pendingAmount: number;
}
export interface BatchPayLineDetails {
  totalAmountExclusiveHoliday: number;
  totalHolidayAmount: number;
  payLinesDetails: PayLinesDetails[];
}

export interface PayLinesDetails {
  amountExclusiveHoliday: number;
  firstName: string;
  lastName: string;
  holiday: number;
  payCode: string;
  payElement: string;
  rateExclusiveHoliday: number;
  unit: number;
  weekending: string;
}
